import { createApiInstance } from "./api";

const instance = createApiInstance("/widget-config");

// Os contextos de página não são definições do widget (RPAA-247): lêem-se e
// escrevem-se só pelas rotas `/page-contexts`, e o PUT das definições ignora um
// `copy.pageContexts` que lhe chegue. Um backend anterior ainda os devolve no
// GET e na resposta do PUT, todos, sem tecto: com os 8.300 da Radio Popular são
// mais de 3 MB. Saem aqui, antes de chegarem a quem chama, porque tudo o que lê
// estas definições as guarda: o formulário da página, que as serializa a cada
// tecla no `hasChanges` e as manda ao iframe da pré-visualização, e a cache da
// transcrição das Conversas, que as guarda por chat até ao fim da sessão.
const withoutPageContexts = (settings) => {
  if (!settings?.copy) return settings;
  const copy = { ...settings.copy };
  delete copy.pageContexts;
  return { ...settings, copy };
};

export const widgetConfigApi = {
  /**
   * Fetch current widget settings for a chat, without the page contexts.
   * @param {string} chatId
   * @returns {Promise<{theme, copy, features}>}
   */
  get: async (chatId) => {
    const r = await instance.get(`/${chatId}`);
    return withoutPageContexts(r.data);
  },

  /**
   * Partial-update widget settings. The endpoint accepts any subset of
   * { theme, copy, features, locale }. Returns the merged current state,
   * without the page contexts.
   * @param {string} chatId
   * @param {{theme?, copy?, features?, locale?}} payload
   */
  update: async (chatId, payload) => {
    const r = await instance.put(`/${chatId}`, payload);
    return withoutPageContexts(r.data);
  },

  // --- Contextos de página (RPAA-247) ---
  //
  // Os contextos saíram do payload de `update`: são milhares num tenant a sério,
  // e enviá-los todos a cada gravação era o que tornava o editor inutilizável.
  // Cada um tem agora endereço próprio, e o Save da página deixa de os
  // transportar, e portanto deixa de os poder perder.

  /**
   * Uma página de contextos, pela ordem de precedência.
   * @param {string} chatId
   * @param {{offset?: number, limit?: number, q?: string}} opts
   * @returns {Promise<{items: Array, total: number}>}
   */
  listPageContexts: async (chatId, { offset = 0, limit = 50, q } = {}) => {
    const params = { offset, limit };
    if (q) params.q = q;
    const r = await instance.get(`/${chatId}/page-contexts`, { params });
    return r.data;
  },

  /** Cria na precedência mais baixa. Devolve { id, position }. */
  createPageContext: async (chatId, context) => {
    const r = await instance.post(`/${chatId}/page-contexts`, context);
    return r.data;
  },

  /** Substituição total do contexto: os padrões que não vierem são apagados. */
  updatePageContext: async (chatId, contextId, context) => {
    const r = await instance.patch(`/${chatId}/page-contexts/${contextId}`, context);
    return r.data;
  },

  deletePageContext: async (chatId, contextId) => {
    await instance.delete(`/${chatId}/page-contexts/${contextId}`);
  },

  /**
   * Download this chat's page contexts as a JSON file.
   *
   * The name comes from the server's Content-Disposition, which carries the
   * chat's slug and the date — page contexts are per chat, so two files from
   * the same tenant are otherwise indistinguishable in a Downloads folder. The
   * local fallback only covers the header not arriving.
   *
   * Exports what is SAVED, not what is on screen.
   * @param {string} chatId
   */
  exportPageContexts: async (chatId) => {
    const response = await instance.get(`/${chatId}/page-contexts/export`, {
      responseType: "blob",
    });

    const contentDisposition = response.headers["content-disposition"];
    const today = new Date().toISOString().split("T")[0];
    let filename = `quick-replies_${today}.json`;
    if (contentDisposition) {
      // [^";]+ rather than .+ so a quoted filename does not keep its closing
      // quote, and a parameter after it does not get swallowed.
      const match = contentDisposition.match(/filename="?([^";]+)"?/i);
      if (match) {
        filename = match[1];
      }
    }

    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/json" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  },

  /**
   * Replace this chat's page contexts with the ones in the file.
   *
   * REPLACES the whole list — there is no stable key to merge on. A single bad
   * entry refuses the whole file and writes nothing; the full list of errors
   * arrives in `err.response.data.detail`, one per line, each naming the entry
   * by index. On success the body carries `imported_count` and `warnings`,
   * which are codes to be translated here, not sentences from the backend.
   * @param {string} chatId
   * @param {File} file
   */
  importPageContexts: async (chatId, file) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await instance.post(
      `/${chatId}/page-contexts/import`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },
};
